import {
  Crown,
  TrendingUp,
  Handshake,
  Sparkles,
  Compass,
  Users,
  ArrowUpRight,
} from "lucide-react";
import { WHATSAPP_HREF } from "@/lib/ascentix";

const programmes = [
  {
    id: "leadership-development",
    icon: Crown,
    title: "Leadership Development",
    desc: "Equipping leaders with the skills, confidence and strategic judgement required to drive business success, manage teams and make stronger decisions.",
    accent: "from-orange-500 to-amber-500",
    pattern: "peaks",
  },
  {
    id: "business-development",
    icon: TrendingUp,
    title: "Business Development",
    desc: "Building capability to identify opportunities, grow revenue, strengthen market presence and support long-term organisational growth.",
    accent: "from-amber-500 to-orange-600",
    pattern: "rise",
  },
  {
    id: "relationship-management",
    icon: Handshake,
    title: "Relationship Management",
    desc: "Helping teams build stronger relationships with clients, stakeholders and colleagues through trust, communication and customer-centred practice.",
    accent: "from-orange-600 to-rose-500",
    pattern: "rings",
  },
  {
    id: "digital-literacy",
    icon: Sparkles,
    title: "Digital Literacy & AI Readiness",
    desc: "Supporting organisations with the skills needed to adopt digital tools, improve efficiency and prepare teams for modern ways of working.",
    accent: "from-orange-500 to-yellow-500",
    pattern: "grid",
  },
  {
    id: "coaching-mentoring",
    icon: Users,
    title: "Coaching & Mentoring",
    desc: "Providing structured support for professionals and managers to improve performance, confidence, communication and leadership maturity.",
    accent: "from-amber-600 to-orange-500",
    pattern: "rings",
  },
  {
    id: "facilitation-consulting",
    icon: Compass,
    title: "Facilitation & Consulting",
    desc: "Helping organisations align teams, solve business challenges and deliver more effective meetings, workshops and strategic sessions.",
    accent: "from-orange-500 to-amber-400",
    pattern: "peaks",
  },
];

function ProgrammeArt({ pattern }) {
  if (pattern === "peaks") {
    return (
      <svg viewBox="0 0 200 120" className="absolute inset-0 w-full h-full opacity-90">
        <polygon points="100,18 195,114 5,114" fill="rgba(255,255,255,0.10)" />
        <polygon points="100,40 170,114 30,114" fill="rgba(255,255,255,0.16)" />
        <polygon points="100,62 145,114 55,114" fill="rgba(255,255,255,0.28)" />
      </svg>
    );
  }
  if (pattern === "rise") {
    return (
      <svg viewBox="0 0 200 120" className="absolute inset-0 w-full h-full opacity-90">
        <polyline points="0,100 40,80 80,90 120,55 160,40 200,15"
          fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="200" cy="15" r="5" fill="rgba(255,255,255,0.95)" />
        {[40, 80, 120, 160].map((x, i) => (
          <circle key={i} cx={x} cy={[80, 90, 55, 40][i]} r="3" fill="rgba(255,255,255,0.55)" />
        ))}
      </svg>
    );
  }
  if (pattern === "rings") {
    return (
      <svg viewBox="0 0 200 120" className="absolute inset-0 w-full h-full opacity-90">
        {[60, 45, 30, 15].map((r, i) => (
          <circle key={i} cx="150" cy="60" r={r}
            fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" />
        ))}
        <circle cx="60" cy="60" r="14" fill="rgba(255,255,255,0.85)" />
      </svg>
    );
  }
  // grid
  return (
    <svg viewBox="0 0 200 120" className="absolute inset-0 w-full h-full opacity-90">
      <defs>
        <pattern id="dots" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.4" fill="rgba(255,255,255,0.55)" />
        </pattern>
      </defs>
      <rect width="200" height="120" fill="url(#dots)" />
      <rect x="115" y="22" width="70" height="80" rx="8"
        fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" />
      <rect x="125" y="34" width="50" height="3" rx="1.5" fill="rgba(255,255,255,0.85)" />
      <rect x="125" y="44" width="38" height="3" rx="1.5" fill="rgba(255,255,255,0.6)" />
      <rect x="125" y="54" width="44" height="3" rx="1.5" fill="rgba(255,255,255,0.6)" />
    </svg>
  );
}

export default function Programmes() {
  const enquireUrl = (title) =>
    `https://wa.me/2349068077777?text=${encodeURIComponent(
      `Hello Ascentix, I'd like to enquire about your "${title}" programme.`
    )}`;

  return (
    <section
      id="programmes"
      data-testid="programmes-section"
      className="relative py-24 md:py-32 bg-[#f4f1ea]/40"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-16">
        <div className="max-w-3xl reveal">
          <span className="eyebrow">What We Do</span>
          <h2
            data-testid="programmes-headline"
            className="font-display mt-4 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-slate-900"
          >
            Programmes that build{" "}
            <span className="text-orange-600">real capability</span>.
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-slate-600 max-w-[620px]">
            Six practice areas — designed, facilitated and adapted around your
            organisation's stage, sector and strategic priorities.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {programmes.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                data-testid={`programme-card-${p.id}`}
                className="lift-card reveal rounded-3xl bg-white border border-slate-900/5 overflow-hidden flex flex-col h-full"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div
                  className={`relative aspect-[16/10] overflow-hidden bg-gradient-to-br ${p.accent}`}
                >
                  <ProgrammeArt pattern={p.pattern} />
                  <div className="absolute top-3 left-3 size-12 rounded-2xl bg-white/95 backdrop-blur border border-white/60 flex items-center justify-center text-orange-600 shadow-sm">
                    <Icon className="size-6" strokeWidth={1.7} />
                  </div>
                  <span className="absolute top-3 right-3 text-[11px] font-semibold text-white/95 bg-slate-900/35 backdrop-blur px-2.5 py-1 rounded-full">
                    0{i + 1}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-display text-[22px] leading-tight text-slate-900">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600 flex-1">
                    {p.desc}
                  </p>
                  <a
                    href={enquireUrl(p.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`programme-enquire-${p.id}`}
                    className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-orange-600 hover:text-orange-700 group"
                  >
                    Enquire About This
                    <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="programmes-cta-tailored"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
          >
            Need a tailored programme? Talk to us
            <ArrowUpRight className="size-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
